import React, { useState } from 'react';
import slugify from 'slugify';

const appleFont = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
};

const ProductCard = ({ product, addToCart, toggleFavorite, isFavorite, cartItems, navigate }) => {
  const [addedToCart, setAddedToCart] = useState(false);
  const [imgError, setImgError] = useState(false);

  const isInCart = cartItems?.some((item) => item.id === product.id);
  const favorited = isFavorite ? isFavorite(product.id) : false;
  const freeDelivery = product.PCW === 0;

  const imageSrc = imgError
    ? 'https://via.placeholder.com/300x300/f5f5f5/cccccc?text=No+Image'
    : product.image || (product.images && product.images[0]) || 'https://via.placeholder.com/300x300/f5f5f5/cccccc?text=No+Image';

  const discountPercent = product.discount
    ? product.discount
    : product.originalPrice && product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (addToCart) {
      addToCart(product);
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 1500);
    }
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();

    // localStorage থেকে current favorites নাও
    const saved = JSON.parse(localStorage.getItem('favorites') || '[]');
    const productId = product.id;

    let updated;
    if (saved.some(fav => String(fav.id) === String(productId))) {
      // আগে থেকে আছে → remove করো
      updated = saved.filter(fav => String(fav.id) !== String(productId));
    } else {
      // নেই → add করো (পুরো product save করো)
      updated = [...saved, product];
    }

    localStorage.setItem('favorites', JSON.stringify(updated));

    // UI update এর জন্য parent toggleFavorite ও call করো
    if (toggleFavorite) toggleFavorite(product.id);
  };

  const handleCardClick = () => {
    if (navigate) {
      const titleSlug = slugify(product.name || '', { lower: true, strict: true, trim: true });
      navigate(`/product/${product.id}/${titleSlug}`);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="relative bg-white overflow-hidden cursor-pointer group flex flex-col h-full"
      style={{
        ...appleFont,
        border: '1px solid #eee',
        transition: 'box-shadow 0.22s ease, transform 0.22s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.13)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* ───── IMAGE SECTION ───── */}
      <div className="relative overflow-hidden bg-gray-50" style={{ aspectRatio: '1 / 1' }}>
        <img
          src={imageSrc}
          alt={product.name}
          onError={() => setImgError(true)}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />

        {/* Discount Badge */}
        {discountPercent > 0 && (
          <span
            className="absolute top-2 left-2 text-white font-bold px-2 py-0.5"
            style={{ backgroundColor: '#ff3f6c', fontSize: '11px' }}
          >
            -{discountPercent}%
          </span>
        )}

        {/* Free Delivery Badge */}
        {freeDelivery && (
          <span
            className="absolute bottom-2 left-2 text-white font-bold px-2 py-0.5 flex items-center gap-1"
            style={{ backgroundColor: '#2bb673', fontSize: '10px' }}
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
              <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3a2 2 0 001.664-.89l1.5-2.25A2 2 0 0017 9.75V7a1 1 0 00-1-1h-3V5a1 1 0 00-1-1H3z" />
            </svg>
            Free Delivery
          </span>
        )}

        {/* ── Wishlist Button (সবসময় দৃশ্যমান, hover-এ পরিষ্কার) ── */}
        <button
          onClick={handleToggleFavorite}
          title={favorited ? 'Remove from Wishlist' : 'Add to Wishlist'}
          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md z-10 transition-transform duration-200 hover:scale-110"
          style={{ opacity: 1 }}
        >
          <svg
            className="w-4 h-4"
            fill={favorited ? '#ff3f6c' : 'none'}
            stroke={favorited ? '#ff3f6c' : '#999'}
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>

        {/* Hover overlay - Quick Add */}
        <div
          className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-2"
          style={{
            background: 'rgba(0,0,0,0.72)',
            opacity: 0,
            transform: 'translateY(100%)',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
          }}
          ref={(el) => {
            if (!el) return;
            const parent = el.closest('.group');
            if (!parent) return;
            const show = () => { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; };
            const hide = () => { el.style.opacity = '0'; el.style.transform = 'translateY(100%)'; };
            parent.addEventListener('mouseenter', show);
            parent.addEventListener('mouseleave', hide);
          }}
        >
          <button
            onClick={handleAddToCart}
            className="text-white font-bold uppercase tracking-wider px-5 py-1.5 transition-all duration-200"
            style={{
              border: '1px solid white',
              fontSize: '11px',
              letterSpacing: '0.08em',
              backgroundColor: addedToCart ? '#2bb673' : 'transparent',
            }}
          >
            {addedToCart ? '✓ Added!' : 'Quick Add'}
          </button>
        </div>
      </div>

      {/* ───── DETAILS SECTION ───── */}
      <div className="p-3 flex flex-col flex-1">
        {/* Category tag */}
        {(product.category || product.mainCategory) && (
          <p
            className="uppercase tracking-wider mb-1 truncate"
            style={{ color: '#2bb673', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em' }}
          >
            {product.category || product.mainCategory}
          </p>
        )}

        {/* Product Name */}
        <h3
          className="font-medium leading-snug mb-2 line-clamp-2 flex-1"
          style={{ color: '#282c3f', fontSize: '13px', lineHeight: '1.4' }}
        >
          {product.name}
        </h3>

        {/* Price Row */}
        <div className="flex items-baseline gap-2 flex-wrap mb-3">
          <span className="font-bold" style={{ color: '#282c3f', fontSize: '15px' }}>
            ৳{Number(product.price).toLocaleString()}
          </span>
          {product.originalPrice && product.originalPrice > product.price && (
            <>
              <span className="line-through" style={{ color: '#aaa', fontSize: '12px' }}>
                ৳{Number(product.originalPrice).toLocaleString()}
              </span>
              {discountPercent > 0 && (
                <span className="font-bold" style={{ color: '#ff3f6c', fontSize: '12px' }}>
                  ({discountPercent}% off)
                </span>
              )}
            </>
          )}
        </div>

        {/* ── Wishlist Button (card-এর নিচে, Add to Cart এর উপরে) ── */}
        <button
          onClick={handleToggleFavorite}
          className="w-full py-2 mb-2 font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-200 border"
          style={{
            fontSize: '11px',
            letterSpacing: '0.07em',
            backgroundColor: favorited ? '#fff0f3' : '#fff',
            color: favorited ? '#ff3f6c' : '#666',
            borderColor: favorited ? '#ff3f6c' : '#ddd',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#ff3f6c';
            e.currentTarget.style.color = '#ff3f6c';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = favorited ? '#ff3f6c' : '#ddd';
            e.currentTarget.style.color = favorited ? '#ff3f6c' : '#666';
          }}
        >
          <svg
            className="w-3.5 h-3.5"
            fill={favorited ? '#ff3f6c' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          {favorited ? 'Wishlisted' : 'Add to Wishlist'}
        </button>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          className="w-full py-2 font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors duration-200 mt-auto"
          style={{
            backgroundColor: addedToCart ? '#2bb673' : isInCart ? '#444' : '#212121',
            color: '#fff',
            fontSize: '11px',
            letterSpacing: '0.07em',
            border: 'none',
          }}
          onMouseEnter={(e) => {
            if (!addedToCart) e.currentTarget.style.backgroundColor = '#ff3f6c';
          }}
          onMouseLeave={(e) => {
            if (!addedToCart) e.currentTarget.style.backgroundColor = isInCart ? '#444' : '#212121';
          }}
        >
          {addedToCart ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Added!
            </>
          ) : isInCart ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              In Cart
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Add to Cart
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;